'use client'

import { type ReactNode, useRef, useState } from 'react'

import dynamic from 'next/dynamic'

import { useCopyArticleLink } from '@/hooks/client/use-copy-article-link'
import { useTranslation } from '@/hooks/client/use-translation'
import SvgCheck from '@/icons/regular/Check'
import SvgLink from '@/icons/regular/Link'
import SvgPrint from '@/icons/regular/Print'
import SvgShareNodes from '@/icons/regular/ShareNodes'

const ShareDialog = dynamic(() => import('./share-dialog').then((module) => module.ShareDialog), { ssr: false })

const buttonClasses =
  'text-navy hover:bg-navy/5 flex min-h-[44px] cursor-pointer items-center justify-center gap-2 rounded-full border border-navy/20 px-3 text-[15px] leading-5 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lapis sm:px-4'

interface ShareButtonsProps {
  title: string
  className?: string
  /** Rendered first in the button row (the article footer's preferred-source pill). */
  leading?: ReactNode
}

export const ShareButtons = ({ title, className, leading }: ShareButtonsProps) => {
  const { t } = useTranslation()
  const { copyStatus, copyArticleLink } = useCopyArticleLink()
  const [shareOpen, setShareOpen] = useState(false)
  const shareButton = useRef<HTMLButtonElement>(null)

  const closeShare = () => {
    setShareOpen(false)
    // The shared modal has no Radix Trigger; restore focus after it unmounts.
    requestAnimationFrame(() => shareButton.current?.focus({ preventScroll: true }))
  }

  return (
    <div className={`share-buttons flex min-w-0 flex-col items-start gap-3 ${className ?? ''}`}>
      <div className="flex flex-wrap items-center gap-2">
        {leading}
        <button onClick={copyArticleLink} className={buttonClasses} type="button" data-tracking="ignore">
          {copyStatus === 'copied' ? (
            <SvgCheck className="size-[15px] shrink-0 fill-current" aria-hidden="true" />
          ) : (
            <SvgLink className="size-[15px] shrink-0 fill-current" aria-hidden="true" />
          )}
          <span className="grid">
            <span className={`col-start-1 row-start-1 ${copyStatus === 'copied' ? 'invisible' : ''}`}>
              {t('client.article.copy', 'Copy')}
            </span>
            <span className={`col-start-1 row-start-1 ${copyStatus === 'copied' ? '' : 'invisible'}`}>
              {t('client.article.copied', 'Copied')}
            </span>
          </span>
        </button>
        <button
          ref={shareButton}
          onClick={() => setShareOpen(true)}
          className={buttonClasses}
          type="button"
          aria-haspopup="dialog"
          aria-expanded={shareOpen}
          data-tracking="ignore"
        >
          <SvgShareNodes className="size-[15px] shrink-0 fill-current" aria-hidden="true" />
          {t('client.article.share', 'Share')}
        </button>
        <button onClick={() => window.print()} className={buttonClasses} type="button" data-tracking="ignore">
          <SvgPrint className="size-[15px] shrink-0 fill-current" aria-hidden="true" />
          {t('client.article.print', 'Print')}
        </button>
      </div>
      <span role="status" className={copyStatus === 'error' ? 'text-navy text-[15px] leading-5' : 'sr-only'}>
        {copyStatus === 'copied'
          ? t('client.article.linkCopied', 'Link copied')
          : copyStatus === 'error'
            ? t('client.article.copyFailed', 'Could not copy the link. Please try again.')
            : ''}
      </span>
      {shareOpen && <ShareDialog title={title} onClose={closeShare} />}
    </div>
  )
}
